'use client';
import { fetchCustomerAccount } from '@/api/account';
import {
  Banner,
  Faq,
  HowItWorks,
  JoinAndEarnRewards,
  YourPoints,
} from '@/app/zeelool-rewards/components';
import ExclusiveRewards from '@/app/zeelool-rewards/components/ExclusiveRewards';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { sensorsTrack } from '@/utils/sensors';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import MemberLevel from './components/MemberLevel';
import ReferFriend from './components/ReferFriend';
import RewardItems from './components/RewardItems';
import YouPoints from './components/YouPoints';

export default function MobileZeeloolRewards({
  memberLevelConfig,
  memberPrivileges,
}: {
  memberLevelConfig: any;
  memberPrivileges: any;
}) {
  const router = useRouter();
  const userInfo = useStore((state) => state.userInfo);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const token = useStore((state) => state.token);
  const t = useTranslations('zeeloolRewards');
  const [loginType, setLoginType] = useState<string>('login');
  const setJoinFreeDialogInfo = useStore((state) => state.setJoinFreeDialogInfo);
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const { cnName } = useRouteMetadata();
  const pathname = usePathname();
  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });
  const SDRewardsPageBtnClick = (btnName: string) => {
    sensorsTrack('RewardsPageBtn_Click', {
      btn_name: btnName,
      page_name: '会员中心页',
    });
  };

  const handleLogin = () => {
    setShowLoginRegisterDialog(true);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.event_name === 'loginSuccess') {
        fetchCustomerAccount().then((res) => {
          setUserInfo(res.data);
          if (res.data.is_member === 2 && loginType === 'join') {
            setJoinFreeDialogInfo({
              show: true,
            });
          } else if (loginType === 'join') {
            router.push('/account/zeelool-rewards');
          }
        });
      }
    };

    window.addEventListener('message', handleMessage);

    // 组件卸载时移除事件监听
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [loginType]);

  // 页面离开事件追踪
  const trackPageLeave = useCallback(() => {
    try {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const viewDuration = Math.ceil((Date.now() - enterTimer.current) / 1000);
      const { viewAdvance, viewArea } = lastScrollState.current;

      trackEvent(
        {
          eventName: 'RewardsPage_Leave',
          properties: {
            page_name: cnName,
            refer_page_name: refer_page_name || '',
            refer_url_path: refer_url_path || '',
            view_duration: viewDuration,
            view_advance: viewAdvance,
            view_area: viewArea,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('RewardsPage_Leave tracking error:', error);
    }
  }, [trackEvent, cnName]);

  // 更新滚动状态
  const updateScrollState = useCallback(() => {
    try {
      const viewAdvance = getCurrentScrollPercentage();
      const viewArea = getViewArea();
      lastScrollState.current = { viewAdvance, viewArea };
    } catch (error) {
      logger.error('Error updating scroll state:', error);
    }
  }, []);

  // 页面浏览事件追踪
  useEffect(() => {
    enterTimer.current = Date.now();
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    trackEvent(
      {
        eventName: 'RewardsPage_View',
        properties: {
          page_name: cnName,
          refer_page_name: refer_page_name || '',
          refer_url_path: refer_url_path || '',
        },
      },
      [ProviderType.SENSORS],
    );
  }, [cnName]);

  // 监听滚动事件，更新滚动状态
  useEffect(() => {
    // 立即更新一次
    updateScrollState();

    // 监听滚动事件
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollState]);

  // 监听路由变化，触发离开事件
  useEffect(() => {
    return () => {
      trackPageLeave();
    };
  }, [pathname, trackPageLeave]);

  const handleJoinNow = () => {
    SDRewardsPageBtnClick('Join Now');
    setLoginType('join');
    logger.log('handleJoinNow', userInfo.is_member, token);
    if (!token) {
      setShowLoginRegisterDialog(true);
      return;
    }

    if (userInfo.is_member === 2) {
      setJoinFreeDialogInfo({
        show: true,
      });
    } else {
      router.push('/account/zeelool-rewards');
    }
  };

  return (
    <>
      {userInfo.member_level > -1 ? (
        <div className="mt-[10px]">
          <div className="header flex flex-col items-center justify-between px-14">
            <MemberLevel
              memberLevelConfig={memberLevelConfig}
              memberPrivileges={memberPrivileges}
              SDRewardsPageBtnClick={SDRewardsPageBtnClick}
            />

            <YouPoints SDRewardsPageBtnClick={SDRewardsPageBtnClick} />
          </div>
          <div className="container mx-auto mt-[52px]">
            <h2 className="text-[24px] leading-[29px] font-bb text-center mb-20">
              {t('account.exclusiveRewards')}
            </h2>
            <RewardItems
              memberLevelConfig={memberLevelConfig}
              memberPrivileges={memberPrivileges}
              SDRewardsPageBtnClick={SDRewardsPageBtnClick}
            />
            <ExclusiveRewards
              memberLevelConfig={memberLevelConfig}
              memberPrivileges={memberPrivileges}
              userInfo={userInfo}
              showTitle={false}
            />
          </div>
          <HowItWorks
            memberPrivileges={memberPrivileges}
            showBtn
            SDRewardsPageBtnClick={SDRewardsPageBtnClick}
          />
          <ReferFriend SDRewardsPageBtnClick={SDRewardsPageBtnClick} />
          <Faq memberPrivileges={memberPrivileges} />
        </div>
      ) : (
        <div>
          <Banner
            memberPrivileges={memberPrivileges}
            SDRewardsPageBtnClick={SDRewardsPageBtnClick}
            joinNowClick={handleJoinNow}
          />
          <HowItWorks
            memberPrivileges={memberPrivileges}
            SDRewardsPageBtnClick={SDRewardsPageBtnClick}
          />
          <YourPoints
            memberPrivileges={memberPrivileges}
            handleLogin={handleLogin}
            handleJoinNow={handleJoinNow}
          />
          <ExclusiveRewards
            memberPrivileges={memberPrivileges}
            memberLevelConfig={memberLevelConfig}
            userInfo={userInfo}
          />
          <JoinAndEarnRewards
            memberPrivileges={memberPrivileges}
            isWeb={false}
            handleJoinNow={handleJoinNow}
          />
          <Faq memberPrivileges={memberPrivileges} />
        </div>
      )}
    </>
  );
}
